/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import UiIcon from 'components/Ui/UiIcon'
import UiStateView from 'components/Ui/UiStateView'

export type UiLoaderVariant = 'bar' | 'ellipsis' | 'spinner' | 'page'

interface UiLoaderProps {
    variant?: UiLoaderVariant
    className?: string
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

const UiLoader: React.FC<UiLoaderProps> = ({ variant = 'spinner', className, size = 'md' }) => {
    const sizes = {
        xs: 'size-3',
        sm: 'size-4',
        md: 'size-8',
        lg: 'size-12',
        xl: 'size-16',
    }

    if (variant === 'bar') {
        return (
            <div className={clsx('UiLoader relative h-1 w-full overflow-hidden bg-gray-100 dark:bg-gray-800', className)}>
                <div className="absolute top-0 h-full w-full animate-progress bg-arbo-orange" />
            </div>
        )
    }

    if (variant === 'ellipsis') {
        return (
            <div className={clsx('UiLoader flex items-center justify-center gap-1.5', className)}>
                <div className="h-2 w-2 animate-bounce rounded-full bg-current opacity-70" style={{ animationDelay: '0ms' }} />
                <div className="h-2 w-2 animate-bounce rounded-full bg-current opacity-70" style={{ animationDelay: '150ms' }} />
                <div className="h-2 w-2 animate-bounce rounded-full bg-current opacity-70" style={{ animationDelay: '300ms' }} />
            </div>
        )
    }

    if (variant === 'page') {
        return (
            <UiStateView
                variant="loading"
                className={clsx('UiLoader min-h-50', className)}
            />
        )
    }

    return (
        <div className={clsx('UiLoader flex items-center justify-center', className)}>
            <UiIcon name="loader" className={clsx('animate-spin', sizes[size])} />
        </div>
    )
}

export default UiLoader
